import React from 'react'
import {
  ADMIN_REPORTS_PATH,
  ADMIN_BOOK_INDICATION_PATH,
  ADMIN_MANAGE_COLLECTION_PATH,
  ADMIN_ENTITY_USERS_V2_PATH,
  ADMIN_LEAGUE_REPORT,
  ADMIN_JOB_POSITIONS_AND_PERMISSIONS,
  ADMIN_BNCC_DIDACTIC_SEQUENCE_PATH,
  ADMIN_ACTIVITIES_HOME_PATH,
  ADMIN_READING_ASSESSMENT_HOME_V2,
  ADMIN_READING_FLUENCY_APP_V2,
  ADMIN_TIME_USAGE_REPORTS_PATH,
  ADMIN_SUMMARY_REPORT_V3_PATH
} from 'routes/paths'
import {
  StyledSidebar,
  Logo,
  SidebarItem,
  SidebarContent
} from './Sidebar.styles'
import { FontIcon } from 'components/lib/icons'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'i18n'
import { Cdn } from 'utils/assets'
import { useHistory, useLocation } from 'react-router-dom'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { FontIcon as BonsaiFontIcon } from '@arvoreeducacao/bonsai-ds'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import { useSuperAutorEligible } from 'utils/hooks/use-super-autor-eligible/useSuperAutorEligible'
import usePermissions from 'utils/hooks/use-permissions'

function Sidebar({ minimize }: { minimize?: boolean }) {
  const location = useLocation()
  const { canManagePermissions, canManageCollections } = usePermissions()
  const history = useHistory()
  const { user } = useUserInfo()
  const { t } = useTranslation()
  const { featureToggles } = useFeatureToggles()
  const { isSuperAutorEligible } = useSuperAutorEligible()

  const goToPath = path => {
    history.push(path)
  }

  return (
    <StyledSidebar $minimize={false}>
      {minimize && <ReactTooltip />}
      <Logo $minimize={false} href={ADMIN_SUMMARY_REPORT_V3_PATH}>
        <img src={Cdn.getImageUrl('logos/icon-livros-round.svg')} />
      </Logo>

      <SidebarContent>
        <SidebarItem
          $minimize={false}
          onClick={() => {
            window.location.href = ADMIN_SUMMARY_REPORT_V3_PATH
          }}
          data-tip={t('Resumo')}
          style={user.isClientSomos ? { display: 'none' } : {}}
        >
          <FontIcon size={24} iconName="home" />
          {t('Resumo')}
        </SidebarItem>
        {user.canAccessUsageReports && (
          <SidebarItem
            $minimize={false}
            data-tip={t('Relatórios')}
            onClick={() => goToPath(ADMIN_REPORTS_PATH)}
            active={location.pathname.startsWith(ADMIN_REPORTS_PATH)}
            style={user.isClientSomos ? { display: 'none' } : {}}
          >
            <FontIcon size={24} iconName="equalizer" />
            {t('Relatórios')}
          </SidebarItem>
        )}
        {!user?.isContextNonEducational && !user?.featureToggles?.activitiesV3 && (
          <SidebarItem
            $minimize={false}
            onClick={() => goToPath(ADMIN_BOOK_INDICATION_PATH)}
            active={location.pathname === ADMIN_BOOK_INDICATION_PATH}
            data-tip={t('Indicar Livro')}
            style={
              user.isClientSomos || !user.canAccessIndications
                ? { display: 'none' }
                : {}
            }
          >
            <FontIcon size={24} iconName="local_library" />
            {t('Indicar Livro')}
          </SidebarItem>
        )}
        {user.canAccessActivities && (
          <SidebarItem
            $minimize={false}
            onClick={() => {
              goToPath(ADMIN_ACTIVITIES_HOME_PATH)
            }}
            active={location.pathname === ADMIN_ACTIVITIES_HOME_PATH}
            data-tip={t('Tarefas')}
          >
            <FontIcon size={24} iconName="assignment" />
            {t('Tarefas')}
          </SidebarItem>
        )}

        {user.canAccessReadingFluency && (
          <SidebarItem
            $minimize={false}
            onClick={() => {
              if (user?.featureToggles?.readAloudV2Leveling) {
                window.location.href = ADMIN_READING_FLUENCY_APP_V2
              } else {
                goToPath(ADMIN_READING_ASSESSMENT_HOME_V2)
              }
            }}
            active={
              location.pathname.indexOf(ADMIN_READING_ASSESSMENT_HOME_V2) > -1
            }
            data-tip={t('Fluência leitora')}
            style={user.isClientSomos ? { display: 'none' } : {}}
          >
            <BonsaiFontIcon size={24} iconName="content-check" />
            {t('Fluência leitora')}
          </SidebarItem>
        )}
        {featureToggles.usageTime && (
          <SidebarItem
            $minimize={false}
            onClick={() => goToPath(ADMIN_TIME_USAGE_REPORTS_PATH)}
            active={
              location.pathname.indexOf(ADMIN_TIME_USAGE_REPORTS_PATH) > -1
            }
            data-tip={t('Tempo de Uso')}
          >
            <BonsaiFontIcon size={24} iconName="alarm" />
            {t('Tempo de Uso')}
          </SidebarItem>
        )}

        {user.canAccessCustomCollections && canManageCollections && (
          <SidebarItem
            $minimize={false}
            onClick={() => goToPath(ADMIN_MANAGE_COLLECTION_PATH)}
            active={location.pathname === ADMIN_MANAGE_COLLECTION_PATH}
            data-tip={t('Gerenciar Acervo')}
            style={
              user.isClientSomos ||
              (user.reader.root.id === 78452 &&
                user.email !== 'adminsas2020@arvoredelivros.com.br')
                ? { display: 'none' }
                : {}
            }
          >
            <FontIcon size={24} iconName="turned_in" />
            {t('Gerenciar Acervo')}
          </SidebarItem>
        )}
        {user.canAccessEntityUsersManagement && !isSuperAutorEligible && (
          <SidebarItem
            $minimize={false}
            onClick={() => goToPath(ADMIN_ENTITY_USERS_V2_PATH)}
            data-tip={t('Turmas e Usuários')}
          >
            <FontIcon size={24} iconName="people" />
            {t('Turmas e Usuários')}
          </SidebarItem>
        )}
        {user.canAccessLeagueContent && (
          <SidebarItem
            $minimize={false}
            onClick={() => goToPath(ADMIN_LEAGUE_REPORT)}
            active={location.pathname === ADMIN_LEAGUE_REPORT}
            data-tip={t('Liga de leitores')}
            style={user.isClientSomos ? { display: 'none' } : {}}
          >
            <FontIcon size={24} iconName="star" />
            {t('Liga de leitores')}
          </SidebarItem>
        )}

        {user.canAccessDidacticSequences && (
          <SidebarItem
            $minimize={false}
            onClick={() => goToPath(ADMIN_BNCC_DIDACTIC_SEQUENCE_PATH)}
            active={
              location.pathname.indexOf(ADMIN_BNCC_DIDACTIC_SEQUENCE_PATH) > -1
            }
            data-tip={t('Sequências Didáticas')}
            style={user.isClientSomos ? { display: 'none' } : {}}
          >
            <FontIcon size={24} iconName="school" />
            {t('Sequências Didáticas')}
          </SidebarItem>
        )}

        {canManagePermissions && !featureToggles?.unifyRolesAndPermissions && (
          <SidebarItem
            $minimize={false}
            onClick={() => goToPath(ADMIN_JOB_POSITIONS_AND_PERMISSIONS)}
            active={location.pathname === ADMIN_JOB_POSITIONS_AND_PERMISSIONS}
            data-tip={t('Cargos e Permissões')}
          >
            <BonsaiFontIcon size={24} iconName="hierarchy" />
            {t('Cargos e Permissões')}
          </SidebarItem>
        )}
      </SidebarContent>
    </StyledSidebar>
  )
}

export default Sidebar
